import { spawn } from "node:child_process";

export interface SkillManifest {
  name?: string;
  version?: string;
}

async function runCommand(command: string, args: string[]): Promise<string> {
  return new Promise((resolve, reject) => {
    const child = spawn(command, args, { stdio: ["ignore", "pipe", "pipe"] });
    let stdout = "";
    let stderr = "";
    child.stdout.on("data", (chunk: Buffer) => {
      stdout += chunk.toString();
    });
    child.stderr.on("data", (chunk: Buffer) => {
      stderr += chunk.toString();
    });
    child.on("error", reject);
    child.on("close", (code) => {
      if (code === 0) resolve(stdout);
      else reject(new Error(stderr || `Command failed: ${command} ${args.join(" ")}`));
    });
  });
}

function parseSkillFrontmatter(content: string): SkillManifest {
  const match = content.match(/^---\r?\n([\s\S]*?)\r?\n---/);
  if (!match) return {};

  const yaml = match[1];
  const manifest: SkillManifest = {};

  const nameMatch = yaml.match(/^name:\s*["']?([^"'\n]+)["']?\s*$/m);
  if (nameMatch) manifest.name = nameMatch[1].trim();

  const versionMatch = yaml.match(/^version:\s*["']?([^"'\n]+)["']?\s*$/m);
  if (versionMatch) {
    manifest.version = versionMatch[1].trim();
    return manifest;
  }

  const metadataVersionMatch = yaml.match(/^\s+version:\s*["']?([^"'\n]+)["']?\s*$/m);
  if (metadataVersionMatch) {
    manifest.version = metadataVersionMatch[1].trim();
  }

  return manifest;
}

async function findSkillMdPathInZip(zipPath: string): Promise<string | undefined> {
  const listing = await runCommand("unzip", ["-Z1", zipPath]);
  const paths = listing
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
  const matches = paths.filter((item) => /(^|\/)SKILL\.md$/i.test(item));
  if (matches.length === 0) return undefined;
  return matches.sort((a, b) => a.length - b.length)[0];
}

export async function readSkillManifestFromZip(
  zipPath: string | undefined,
): Promise<SkillManifest | undefined> {
  if (!zipPath) return undefined;

  try {
    const skillMdPath = await findSkillMdPathInZip(zipPath);
    if (!skillMdPath) return undefined;
    const stdout = await runCommand("unzip", ["-p", zipPath, skillMdPath]);
    return parseSkillFrontmatter(stdout);
  } catch {
    return undefined;
  }
}
